import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field } from '@/components/ui';
import { env } from '@/config/env';
import { useDoctorPhoto } from '@/features/doctor-profiles/api';
import { isApiError } from '@/lib/api';
import styles from './DoctorProfileDrawer.module.css';

export function DoctorPhotoField({
  staffId,
  photoUrl,
  readOnly,
}: {
  staffId: string;
  photoUrl: string | null;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const photo = useDoctorPhoto(staffId);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>();
  const [requestId, setRequestId] = useState<string>();
  const src = photoUrl?.startsWith('/api/v1/')
    ? `${env.apiBaseUrl}${photoUrl.slice('/api/v1'.length)}`
    : undefined;
  async function save(remove = false) {
    setError(undefined);
    setRequestId(undefined);
    try {
      let encoded: string | null = null;
      if (!remove && file) {
        encoded = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(new Error('read'));
          reader.onload = () => resolve(String(reader.result).split(',')[1]);
          reader.readAsDataURL(file);
        });
      }
      await photo.mutateAsync(encoded);
      setFile(null);
    } catch (err) {
      setError(
        isApiError(err)
          ? err.code === 'DOCTOR_PHOTO_INVALID'
            ? t('doctorPhoto.invalid')
            : err.message
          : t('doctorPhoto.readFailed'),
      );
      if (isApiError(err)) setRequestId(err.requestId);
    }
  }
  return (
    <div className={styles.readField}>
      {src ? (
        <img className={styles.photo} src={src} alt={t('doctorPhoto.label')} />
      ) : (
        <span className={styles.muted}>{t('doctorPhoto.none')}</span>
      )}
      {!readOnly && (
        <>
          <Field
            label={t('doctorPhoto.label')}
            hint={t('doctorPhoto.hint')}
            error={error}
          >
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => {
                const selected = e.target.files?.[0] ?? null;
                setRequestId(undefined);
                if (
                  selected &&
                  !['image/jpeg', 'image/png'].includes(selected.type)
                ) {
                  setError(t('doctorPhoto.type'));
                  setFile(null);
                } else if (
                  selected &&
                  (selected.size === 0 || selected.size > 600 * 1024)
                ) {
                  setError(t('doctorPhoto.size'));
                  setFile(null);
                } else {
                  setError(undefined);
                  setFile(selected);
                }
              }}
            />
          </Field>
          {requestId && (
            <span role="alert">{t('common.requestId', { id: requestId })}</span>
          )}
          <div className={styles.lineActions}>
            <Button
              type="button"
              disabled={!file || photo.isPending}
              onClick={() => void save()}
            >
              {t('doctorPhoto.upload')}
            </Button>
            {src && (
              <Button
                type="button"
                disabled={photo.isPending}
                onClick={() => void save(true)}
              >
                {t('doctorPhoto.remove')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
